// XP sticky pricing table -- stuck state + floating mobile panel.
//
// Loaded only when snippets/form-product.liquid renders with xp_sticky (stock PDPs,
// passed from sections/template-product.liquid). The sticking itself is plain CSS
// `position: sticky` on the original element (assets/custom.css, .xp-pricing-sticky),
// offset by --xp-header-offset, which assets/xp-nav.js owns. This script only:
//   - flags .xp-pricing-sticky--stuck while the element is pinned (desktop shadow,
//     mobile float mode), and
//   - on mobile, floats the open Pricing Details panel over the page while the toggle
//     is stuck, capped so toggle + panel stay within ~40% of the viewport and never
//     reach past the product section, without shifting the page when it switches.
//
// The panel is the original .xp-pricing-collapse, so xp-pricing-table-variant.js
// swatch updates keep reaching its cells while it is open or floating.
//
// The window is the scroller on these pages (the body:has(.xp-pricing-sticky)
// overflow-x: clip rule keeps body from becoming a scroll container at <=1024px),
// so listeners are on window.
(function () {
  var MOBILE = window.matchMedia('(max-width: 992px)');
  var MAX_VIEWPORT_SHARE = 0.4;
  var items = [];
  var ticking = false;

  function isSticky(el) {
    return window.getComputedStyle(el).position === 'sticky';
  }

  // Stuck = displaced from its in-flow position (tracked with a zero-height sentinel
  // placed right before it). Covers both pinned and being pushed out by the end of
  // .product-wrapper.
  function isStuck(item) {
    if (!isSticky(item.el)) return false;
    var marginTop = parseFloat(window.getComputedStyle(item.el).marginTop) || 0;
    var sentinelBottom = item.sentinel.getBoundingClientRect().bottom;
    return item.el.getBoundingClientRect().top > sentinelBottom + marginTop + 0.5;
  }

  function isMobileToggle(item) {
    return MOBILE.matches && item.collapse && item.el.classList.contains('xp-pricing-toggle-wrapper');
  }

  function isOpen(item) {
    return !!(item.collapse && item.collapse.classList.contains('open'));
  }

  // Cap the floating panel: toggle + panel <= ~40vh, and never past the bottom of
  // the product section.
  function updatePanelCap(item) {
    var elRect = item.el.getBoundingClientRect();
    var cap = window.innerHeight * MAX_VIEWPORT_SHARE - elRect.height;
    if (item.section) {
      cap = Math.min(cap, item.section.getBoundingClientRect().bottom - elRect.bottom);
    }
    item.el.style.setProperty('--xp-pricing-panel-max', Math.max(Math.round(cap), 0) + 'px');
  }

  function setFloat(item, on) {
    item.floating = on;
    item.el.classList.toggle('xp-pricing-sticky--float', on);
    if (on) updatePanelCap(item);
  }

  // Panel was opened in flow before sticking: float it and hold its space with a
  // spacer so nothing below moves.
  function floatWithSpacer(item) {
    var styles = window.getComputedStyle(item.collapse);
    var height = item.collapse.offsetHeight +
      (parseFloat(styles.marginTop) || 0) + (parseFloat(styles.marginBottom) || 0);

    var spacer = document.createElement('div');
    spacer.className = 'xp-pricing-sticky-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.height = height + 'px';
    item.el.parentNode.insertBefore(spacer, item.el.nextSibling);
    item.spacer = spacer;

    setFloat(item, true);
  }

  // Panel closed while floating. If its spacer is out of sight (behind the stuck
  // toggle or above the viewport), remove it and scroll by the same amount so the
  // visible page stays put. If it is on screen, remove it without compensating --
  // the content below closes up, same as closing the panel today.
  function unfloat(item) {
    setFloat(item, false);
    var spacer = item.spacer;
    item.spacer = null;
    if (!spacer) return;

    var hidden = spacer.getBoundingClientRect().bottom <= item.el.getBoundingClientRect().bottom + 0.5;
    var anchor = spacer.nextElementSibling;
    var before = anchor ? anchor.getBoundingClientRect().top : 0;
    spacer.parentNode.removeChild(spacer);

    if (hidden && anchor) {
      // Measured after removal so a browser that already scroll-anchored is not
      // compensated twice.
      var delta = anchor.getBoundingClientRect().top - before;
      if (delta) window.scrollBy(0, delta);
    }
  }

  function update(item) {
    var stuck = isStuck(item);
    if (stuck !== item.stuck) {
      item.stuck = stuck;
      item.el.classList.toggle('xp-pricing-sticky--stuck', stuck);
    }

    if (!isMobileToggle(item)) {
      if (item.floating) unfloat(item);
      return;
    }

    // Opened in flow, then scrolled until the toggle stuck: switch to floating.
    // Once floating, it stays floating until the panel closes (switching back to
    // in-flow mid-scroll would shift the page).
    if (isOpen(item) && stuck && !item.floating) floatWithSpacer(item);
    if (item.floating) updatePanelCap(item);
  }

  function updateAll() {
    ticking = false;
    items.forEach(update);
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateAll);
  }

  function setup(el) {
    // Only elements that are direct children of .product-wrapper can stick there:
    // the desktop .table-responsive wrappers, and the mobile toggle after
    // xp-pricing-accordion.js moves it out of .for__mobile.
    var parent = el.parentElement;
    if (!parent || !parent.classList.contains('product-wrapper')) return;
    if (el.__xpSticky) return;

    var sentinel = document.createElement('div');
    sentinel.className = 'xp-pricing-sticky-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    parent.insertBefore(sentinel, el);

    var item = {
      el: el,
      sentinel: sentinel,
      collapse: el.classList.contains('xp-pricing-toggle-wrapper') ? el.querySelector('.xp-pricing-collapse') : null,
      section: el.closest('[data-product-container]') || el.closest('section'),
      stuck: false,
      floating: false,
      spacer: null
    };
    el.__xpSticky = item;
    items.push(item);

    // React to open/close in the same task as xp-pricing-accordion.js's click
    // handler (observer callbacks run before the next paint), so opening while
    // stuck never renders a frame in flow.
    if (item.collapse && window.MutationObserver) {
      new MutationObserver(function () {
        if (!isMobileToggle(item)) return;
        if (isOpen(item)) {
          if (isStuck(item) && !item.floating) setFloat(item, true);
        } else if (item.floating) {
          unfloat(item);
        }
      }).observe(item.collapse, { attributes: true, attributeFilter: ['class'] });
    }

    update(item);
  }

  function init() {
    document.querySelectorAll('.xp-pricing-sticky').forEach(setup);
  }

  // Breakpoint crossed without a reload (DevTools device mode, iPad rotation).
  // xp-pricing-accordion.js moves the mobile toggle in its own change listener;
  // wait a frame so that has run, then drop any float/spacer state (no scroll
  // compensation -- the layout is changing anyway), keep each sentinel right
  // before its element wherever it now lives, and set up anything newly eligible
  // (e.g. the toggle when the page loaded at desktop width).
  function reset(item) {
    if (item.spacer && item.spacer.parentNode) item.spacer.parentNode.removeChild(item.spacer);
    item.spacer = null;
    item.floating = false;
    item.el.classList.remove('xp-pricing-sticky--float');
    item.el.style.removeProperty('--xp-pricing-panel-max');
    if (item.el.previousSibling !== item.sentinel) {
      item.el.parentNode.insertBefore(item.sentinel, item.el);
    }
  }

  function onBreakpointChange() {
    window.requestAnimationFrame(function () {
      items.forEach(reset);
      init();
      updateAll();
    });
  }

  function start() {
    // xp-pricing-accordion.js moves the mobile toggle on DOMContentLoaded; set up
    // after that has run.
    window.requestAnimationFrame(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);
  document.addEventListener('xp:header-offset', requestUpdate);
  // The sticky `top` animates with the mobile nav; re-check once it settles.
  document.addEventListener('transitionend', function (event) {
    if (event.propertyName === 'top' && event.target.classList &&
        event.target.classList.contains('xp-pricing-sticky')) {
      requestUpdate();
    }
  });
  if (MOBILE.addEventListener) {
    MOBILE.addEventListener('change', onBreakpointChange);
  } else if (MOBILE.addListener) {
    MOBILE.addListener(onBreakpointChange);
  }
})();
